import { memo, Children, isValidElement, cloneElement, Fragment, useMemo } from 'react';

import { highlighter } from './Highlight.utils';
import { ErrorBoundary } from '../ErrorBoundary';
import { cn } from '../../lib/styles';

interface SharedHighlightProps {
  highlight: string | string[];
  highlightClassName?: string;
  matchWholeWords?: boolean;
}

interface NonRecursiveHighlightProps extends SharedHighlightProps {
  children: string;
}

interface RecursiveHighlightProps extends SharedHighlightProps {
  children: React.ReactNode;
}

type HighlightComponentProps = NonRecursiveHighlightProps | RecursiveHighlightProps;

const HighlightedText = ({
  text,
  highlight,
  highlightClassName,
  matchWholeWords,
}: SharedHighlightProps & { text: string }) => {
  const highlightChunks = useMemo(
    () => highlighter(text, highlight, matchWholeWords),
    [text, highlight, matchWholeWords],
  );

  return (
    <>
      {highlightChunks.map(({ chunk, highlighted }, i) =>
        highlighted ? (
          <span key={i} data-highlight={chunk} className={cn('bg-yellow-300', highlightClassName)}>
            {chunk}
          </span>
        ) : (
          <Fragment key={i}>{chunk}</Fragment>
        ),
      )}
    </>
  );
};

const NonRecursiveHighlight = ({
  children,
  highlight,
  highlightClassName,
  matchWholeWords,
}: Omit<NonRecursiveHighlightProps, 'recursive'>) => {
  return (
    <HighlightedText
      text={children}
      highlight={highlight}
      highlightClassName={highlightClassName}
      matchWholeWords={matchWholeWords}
    />
  );
};

const RecursiveHighlight = ({
  children,
  highlight,
  highlightClassName,
  matchWholeWords,
}: Omit<RecursiveHighlightProps, 'recursive'>) => {
  const renderRecursively = (childNodes: React.ReactNode): React.ReactNode => {
    return Children.map(childNodes, (child) => {
      if (typeof child === 'string') {
        return (
          <HighlightedText
            text={child}
            highlight={highlight}
            highlightClassName={highlightClassName}
            matchWholeWords={matchWholeWords}
          />
        );
      }

      if (isValidElement(child)) {
        return cloneElement(child, {
          ...child.props,
          children: renderRecursively(child.props.children),
        });
      }

      return (
        <HighlightedText
          text={String(child)}
          highlight={highlight}
          highlightClassName={highlightClassName}
          matchWholeWords={matchWholeWords}
        />
      );
    });
  };

  return <>{renderRecursively(children)}</>;
};

const HighlightBase = (props: HighlightComponentProps) => {
  if (!props.highlight || (Array.isArray(props.highlight) && props.highlight.length === 0)) {
    return props.children;
  }

  if (typeof props.children === 'string') {
    return (
      <NonRecursiveHighlight
        highlight={props.highlight}
        highlightClassName={props.highlightClassName}
        matchWholeWords={props.matchWholeWords}
      >
        {props.children}
      </NonRecursiveHighlight>
    );
  }

  return (
    <ErrorBoundary fallback={props.children}>
      <RecursiveHighlight
        highlight={props.highlight}
        highlightClassName={props.highlightClassName}
        matchWholeWords={props.matchWholeWords}
      >
        {props.children}
      </RecursiveHighlight>
    </ErrorBoundary>
  );
};

export const Highlight = memo(HighlightBase);
